import React, { useMemo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { MANAGER_ROLES, INTERN_ROLES, ROLE_ACCENT } from '@/lib/constants';
import { RoleBadge } from '@/components/common/RoleBadge';
import { LayoutDashboard, KanbanSquare, ClipboardList, BarChart3, Wallet, Download, Users, LogOut, ShieldCheck, User as UserIcon } from 'lucide-react';
import { daysUntil } from '@/lib/constants';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

export function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const visibleItems = useMemo(() => {
    if (!user) return [];
    const isManager = MANAGER_ROLES.has(user.role);
    const isAccountantOrAdmin = ['admin', 'accountant'].includes(user.role);
    const isAdmin = user.role === 'admin';
    return [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, show: true, testid: 'nav-dashboard' },
      { to: '/tasks', label: 'Tasks', icon: KanbanSquare, show: true, testid: 'nav-tasks' },
      { to: '/reports', label: 'Daily Reports', icon: ClipboardList, show: true, testid: 'nav-reports' },
      { to: '/dashboard/weekly', label: 'Weekly Progress', icon: BarChart3, show: true, testid: 'nav-weekly' },
      { to: '/expenses', label: 'Expenses', icon: Wallet, show: isAccountantOrAdmin || user.role === 'ops_manager', testid: 'nav-expenses' },
      { to: '/exports', label: 'Exports', icon: Download, show: isManager, testid: 'nav-exports' },
      { to: '/admin/users', label: 'User Management', icon: Users, show: isAdmin, testid: 'nav-users' },
    ].filter((it) => it.show);
  }, [user]);

  if (!user) return null;

  const isIntern = INTERN_ROLES.has(user.role);
  const days = daysUntil(user.expires_at);
  const accent = ROLE_ACCENT[user.role] || 'var(--primary)';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <aside className="hidden md:flex md:w-64 md:flex-col border-r bg-card" data-testid="sidebar-nav">
      <div className="h-16 flex items-center gap-2 px-5 border-b">
        <div className="h-8 w-8 rounded-md flex items-center justify-center" style={{ backgroundColor: 'hsl(var(--primary))' }}>
          <ShieldCheck className="h-4 w-4 text-white" />
        </div>
        <div className="font-display font-semibold text-base">MSME Ops</div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        <div className="px-2 pb-2 text-[11px] uppercase tracking-wider text-muted-foreground">Workspace</div>
        {visibleItems.map((i) => (
          <NavLink
            key={i.to}
            to={i.to}
            end={i.to === '/dashboard'}
            data-testid={i.testid}
            className={({ isActive }) =>
              `relative flex items-center gap-3 rounded-lg pl-4 pr-3 py-2 text-sm transition-colors duration-150 ${
                isActive
                  ? 'bg-accent/60 text-foreground font-medium'
                  : 'text-muted-foreground hover:bg-accent/40 hover:text-foreground'
              }`
            }
            style={({ isActive }) => (isActive ? { boxShadow: `inset 3px 0 0 hsl(${accent})` } : undefined)}
          >
            <i.icon className="h-4 w-4" />
            <span>{i.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t" data-testid="sidebar-profile-card">
        <div className="flex items-center gap-3 p-2 rounded-lg">
          <Avatar className="h-9 w-9">
            {user.picture ? <AvatarImage src={user.picture} alt={user.name} /> : null}
            <AvatarFallback className="text-xs" style={{ backgroundColor: `hsl(${accent} / 0.15)`, color: `hsl(${accent})` }}>
              {user.name?.split(' ').map((s) => s[0]).slice(0, 2).join('') || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user.name}</div>
            <RoleBadge role={user.role} className="mt-0.5" />
          </div>
        </div>
        {isIntern && user.expires_at ? (
          <div className="mt-2 text-[11px] text-muted-foreground px-2" data-testid="intern-expiry-days-left">
            Access expires in <span className="font-medium text-foreground">{days} days</span>
          </div>
        ) : null}
        <div className="mt-2 flex gap-1">
          <Button variant="ghost" size="sm" className="flex-1 justify-start" onClick={() => navigate('/profile')} data-testid="nav-profile">
            <UserIcon className="h-3.5 w-3.5 mr-2" /> Profile
          </Button>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="sidebar-logout-button" aria-label="Log out">
            <LogOut className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </aside>
  );
}
